import React, { useState } from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, CardBody, CardFooter } from '../components/ui/Card';
import { CtaSection } from '../components/sections/CtaSection';
import { CheckIcon, UsersIcon, CalendarIcon, DollarSignIcon, StarIcon } from 'lucide-react';
export function Packages() {
  const [estimator, setEstimator] = useState({
    groupSize: 30,
    nights: 4,
    resortTier: 'premium',
    activities: 3,
    avLevel: 'standard',
    privateEvents: 2
  });
  const packages = [{
    title: 'Core Retreat',
    description: 'Perfect for team alignment and strategy sessions with focused outcomes.',
    idealFor: 'Leadership teams, department offsites, annual planning',
    nights: '3-4 nights',
    activities: 'Single focused activity',
    resorts: '$300-$500/night',
    inclusions: ['Accommodation in premium resorts', 'Airport transfers', 'Welcome reception', 'Facilitated strategy session', 'Team dinner experience', 'Basic AV setup', 'On-site bilingual concierge'],
    image: 'https://images.unsplash.com/photo-1540541338287-41700207dee6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Incentive Sprints',
    description: 'Reward top performers with an unforgettable experience that drives future success.',
    idealFor: 'Sales teams, high performers, milestone celebrations',
    nights: '3-5 nights',
    activities: '3-5 premium activities',
    resorts: '$500-$1,000/night',
    inclusions: ['Luxury accommodation', 'VIP airport transfers', 'Welcome gifts', 'Multiple excursion options', 'Gala dinner with entertainment', 'Professional photography', 'Personalized concierge service', 'Optional spouse program'],
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Ultra Brand Launch',
    description: 'Create buzz and alignment around your new product or initiative.',
    idealFor: 'Product launches, rebrands, major announcements',
    nights: '3-5 nights',
    activities: 'Multiple branded activations',
    resorts: '$500+ resorts',
    inclusions: ['Premium accommodation', 'Branded welcome experience', 'Custom event app', 'Product showcase setup', 'Media production', 'Multiple themed events', 'Training workshops', 'Press opportunities', 'Full event production'],
    image: 'https://images.unsplash.com/photo-1531058020387-3be344556be6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }];
  const handleEstimatorChange = (field, value) => {
    setEstimator({
      ...estimator,
      [field]: value
    });
  };
  // Simple estimation calculation
  const calculateEstimate = () => {
    const baseCost = {
      standard: 300,
      premium: 500,
      luxury: 800
    }[estimator.resortTier];
    const activityCost = estimator.activities * 150;
    const avCost = {
      basic: 500,
      standard: 1500,
      advanced: 3500
    }[estimator.avLevel];
    const eventCost = estimator.privateEvents * 2000;
    const perPersonPerNight = baseCost + (activityCost + avCost + eventCost) / (estimator.groupSize * estimator.nights);
    const totalLow = Math.round(perPersonPerNight * 0.9 * estimator.groupSize * estimator.nights);
    const totalHigh = Math.round(perPersonPerNight * 1.1 * estimator.groupSize * estimator.nights);
    return {
      perPerson: `$${Math.round(perPersonPerNight * 0.9)}-$${Math.round(perPersonPerNight * 1.1)}`,
      total: `$${totalLow.toLocaleString()}-$${totalHigh.toLocaleString()}`
    };
  };
  const estimate = calculateEstimate();
  return <div className="pt-20 bg-[#F4E8D0]/30" data-id="element-1421">
      {/* Hero */}
      <section className="bg-[#0099A1] py-20" data-id="element-1422">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1423">
          <div className="max-w-3xl" data-id="element-1424">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-1425">
              Packages & Pricing
            </h1>
            <p className="text-xl text-white/90" data-id="element-1426">
              Tailored solutions for your organization's unique goals and
              budget.
            </p>
          </div>
        </div>
      </section>

      {/* Signature Experiences */}
      <section className="py-16 bg-white" data-id="element-1427">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1428">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1429">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-1430">
              Signature Experiences
            </h2>
            <p className="text-lg text-[#374047]/70" data-id="element-1431">
              Choose from our most popular packages, each customizable to your
              specific needs.
            </p>
          </div>
          <div className="space-y-12" data-id="element-1432">
            {packages.map((pkg, index) => <Card key={index} className="overflow-hidden" data-id="element-1433">
                <div className="grid md:grid-cols-3 gap-6" data-id="element-1434">
                  <div className="md:col-span-1" data-id="element-1435">
                    <div className="h-full" data-id="element-1436">
                      <img src={pkg.image} alt={pkg.title} className="w-full h-full object-cover" data-id="element-1437" />
                    </div>
                  </div>
                  <div className="md:col-span-2 p-6" data-id="element-1438">
                    <h3 className="text-2xl font-bold text-[#374047] mb-2" data-id="element-1439">
                      {pkg.title}
                    </h3>
                    <p className="text-[#374047]/70 mb-4" data-id="element-1440">{pkg.description}</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-1441">
                      <div data-id="element-1442">
                        <p className="text-sm text-[#374047]/60" data-id="element-1443">Ideal For:</p>
                        <p className="font-medium" data-id="element-1444">{pkg.idealFor}</p>
                      </div>
                      <div data-id="element-1445">
                        <p className="text-sm text-[#374047]/60" data-id="element-1446">Duration:</p>
                        <p className="font-medium" data-id="element-1447">{pkg.nights}</p>
                      </div>
                      <div data-id="element-1448">
                        <p className="text-sm text-[#374047]/60" data-id="element-1449">
                          Investment Range:
                        </p>
                        <p className="font-medium" data-id="element-1450">{pkg.resorts}</p>
                      </div>
                    </div>
                    <h4 className="text-lg font-semibold text-[#0099A1] mb-3" data-id="element-1451">
                      Inclusions:
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-6" data-id="element-1452">
                      {pkg.inclusions.map((inclusion, i) => <div key={i} className="flex items-start" data-id="element-1453">
                          <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1454" />
                          <span className="ml-2 text-sm" data-id="element-1455">{inclusion}</span>
                        </div>)}
                    </div>
                    <div className="flex flex-wrap gap-3" data-id="element-1456">
                      <Button variant="primary" to="/contact" data-id="element-1457">
                        Request Proposal
                      </Button>
                      <Button variant="outline" data-id="element-1458">Learn More</Button>
                    </div>
                  </div>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Quick Estimator */}
      <section className="py-16 bg-[#F4E8D0]/50" data-id="element-1459">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1460">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1461">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-1462">
              Quick Estimator
            </h2>
            <p className="text-lg text-[#374047]/70" data-id="element-1463">
              Get a ballpark estimate for your event. Adjust the parameters
              below to see how they affect pricing.
            </p>
          </div>
          <Card className="max-w-3xl mx-auto" data-id="element-1464">
            <CardBody data-id="element-1465">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-1466">
                <div data-id="element-1467">
                  <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-1468">
                    Group Size
                  </label>
                  <input type="range" min="10" max="200" value={estimator.groupSize} onChange={e => handleEstimatorChange('groupSize', parseInt(e.target.value))} className="w-full" data-id="element-1469" />
                  <div className="flex justify-between text-sm text-[#374047]/70" data-id="element-1470">
                    <span data-id="element-1471">10</span>
                    <span data-id="element-1472">{estimator.groupSize} people</span>
                    <span data-id="element-1473">200</span>
                  </div>
                </div>
                <div data-id="element-1474">
                  <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-1475">
                    Number of Nights
                  </label>
                  <input type="range" min="2" max="7" value={estimator.nights} onChange={e => handleEstimatorChange('nights', parseInt(e.target.value))} className="w-full" data-id="element-1476" />
                  <div className="flex justify-between text-sm text-[#374047]/70" data-id="element-1477">
                    <span data-id="element-1478">2</span>
                    <span data-id="element-1479">{estimator.nights} nights</span>
                    <span data-id="element-1480">7</span>
                  </div>
                </div>
                <div data-id="element-1481">
                  <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-1482">
                    Resort Tier
                  </label>
                  <select value={estimator.resortTier} onChange={e => handleEstimatorChange('resortTier', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-1483">
                    <option value="standard" data-id="element-1484">Standard ($300-400/night)</option>
                    <option value="premium" data-id="element-1485">Premium ($500-700/night)</option>
                    <option value="luxury" data-id="element-1486">Luxury ($800+/night)</option>
                  </select>
                </div>
                <div data-id="element-1487">
                  <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-1488">
                    Number of Activities
                  </label>
                  <input type="range" min="1" max="10" value={estimator.activities} onChange={e => handleEstimatorChange('activities', parseInt(e.target.value))} className="w-full" data-id="element-1489" />
                  <div className="flex justify-between text-sm text-[#374047]/70" data-id="element-1490">
                    <span data-id="element-1491">1</span>
                    <span data-id="element-1492">{estimator.activities} activities</span>
                    <span data-id="element-1493">10</span>
                  </div>
                </div>
                <div data-id="element-1494">
                  <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-1495">
                    AV/Production Level
                  </label>
                  <select value={estimator.avLevel} onChange={e => handleEstimatorChange('avLevel', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-1496">
                    <option value="basic" data-id="element-1497">Basic (Simple setup)</option>
                    <option value="standard" data-id="element-1498">Standard (Full AV package)</option>
                    <option value="advanced" data-id="element-1499">
                      Advanced (Production-level)
                    </option>
                  </select>
                </div>
                <div data-id="element-1500">
                  <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-1501">
                    Private Events
                  </label>
                  <input type="range" min="0" max="5" value={estimator.privateEvents} onChange={e => handleEstimatorChange('privateEvents', parseInt(e.target.value))} className="w-full" data-id="element-1502" />
                  <div className="flex justify-between text-sm text-[#374047]/70" data-id="element-1503">
                    <span data-id="element-1504">0</span>
                    <span data-id="element-1505">{estimator.privateEvents} events</span>
                    <span data-id="element-1506">5</span>
                  </div>
                </div>
              </div>
              <div className="bg-[#0099A1]/10 rounded-lg p-6" data-id="element-1507">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1508">
                  <div className="text-center" data-id="element-1509">
                    <p className="text-sm text-[#374047]/70 mb-1" data-id="element-1510">
                      Estimated Per Person Per Night
                    </p>
                    <p className="text-2xl font-bold text-[#0099A1]" data-id="element-1511">
                      {estimate.perPerson}
                    </p>
                  </div>
                  <div className="text-center" data-id="element-1512">
                    <p className="text-sm text-[#374047]/70 mb-1" data-id="element-1513">
                      Estimated Total Budget
                    </p>
                    <p className="text-2xl font-bold text-[#0099A1]" data-id="element-1514">
                      {estimate.total}
                    </p>
                  </div>
                </div>
                <p className="text-center text-xs text-[#374047]/70 mt-4" data-id="element-1515">
                  *Estimates are subject to proposal and may vary based on
                  specific requirements, seasonality, and availability.
                </p>
              </div>
            </CardBody>
            <CardFooter className="justify-center" data-id="element-1516">
              <Button variant="primary" to="/contact" data-id="element-1517">
                Request Detailed Proposal
              </Button>
            </CardFooter>
          </Card>
        </div>
      </section>

      <CtaSection data-id="element-1518" />
    </div>;
}